import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

/**
 * Create Application Form PDF
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.data - Form data object containing all application fields
 * @param {string} options.logoPath - Path to logo image
 * @param {string} options.signatureImage - Signature image as data URL
 * @param {string} options.formName - Name of the form
 * @returns {Object} { blob: Blob, base64: string } - PDF as blob and base64
 */
export function createApplicationPDF(options) {
  const { data, logoPath, signatureImage, formName } = options;

  // Apply jsPDF autoTable plugin
  applyPlugin(jsPDF);

  // Initialize PDF document
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  const docHeight = doc.internal.pageSize.getHeight();
  const docWidth = doc.internal.pageSize.getWidth();
  let yPos = 0;

  // Logo configuration
  const logoImg = new Image();
  logoImg.src = logoPath;
  const imageHeight = 345;
  const imageWidth = 1920;
  const logoHeight = 40;
  const logoWidth = (imageWidth / imageHeight) * logoHeight;

  /**
   * Add text to PDF with automatic page breaks
   */
  const addTextToPdf = (text, size, font, x, y, spacing) => {
    doc.setFontSize(size);
    doc.setFont(undefined, font);
    const splitText = doc.splitTextToSize(text, docWidth - 80);
    const textDim = doc.getTextDimensions(splitText);
    const textHeight = textDim.h;

    if (y + textHeight >= docHeight - 20) {
      doc.addPage();
      doc.addImage(
        logoImg,
        "PNG",
        docWidth - logoWidth - 40,
        20,
        logoWidth,
        logoHeight,
        null,
        "SLOW",
      );
      yPos = logoHeight + 45;
      y = yPos;
    }

    doc.text(splitText, x, y);
    yPos = yPos + textHeight + spacing;
  };

  /**
   * Add table with heading to PDF
   */
  const addTwoColumnTableToPdf = (
    heading,
    headingSize,
    tableHeader,
    tableData,
    size,
    font,
    x,
    y,
    spacing,
  ) => {
    // Calculated table height
    const estimatedHeadingTableHeight = tableHeader.length * 21.5 + 40;
    const estimatedTableHeight = tableData.length * 21.5 + 40;

    if (
      y + estimatedTableHeight + estimatedHeadingTableHeight >=
      docHeight - 20
    ) {
      doc.addPage();
      doc.addImage(
        logoImg,
        "PNG",
        docWidth - logoWidth - 40,
        20,
        logoWidth,
        logoHeight,
        null,
        "SLOW",
      );
      yPos = logoHeight + 45;
      y = yPos;
    }

    doc.setFontSize(headingSize);
    doc.autoTable({
      startY: y,
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      styles: {
        cellPadding: 5,
        lineWidth: 1,
        fillColor: [237, 36, 43],
        lineColor: [0, 0, 0],
      },
      head: [tableHeader.map((header) => header)],
      didDrawPage: function (data) {
        yPos = data.cursor.y;
      },
    });

    y = yPos;

    doc.setFontSize(size);
    doc.autoTable({
      startY: y,
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: "auto" },
      },
      styles: {
        cellPadding: 5,
        lineWidth: 1,
        fillColor: [255, 255, 255],
        lineColor: [0, 0, 0],
      },
      body: tableData.map((detail) => [detail.title, detail.data]),
      didDrawPage: function (data) {
        yPos = data.cursor.y + spacing;
      },
    });
  };

  const addFourColumnTableToPdf = (
    tableHeader,
    headerSize,
    tableData,
    size,
    font,
    x,
    y,
    spacing,
  ) => {
    // Calculated table height
    const estimatedHeadingTableHeight = tableHeader.length * 21.5 + 40;
    const estimatedTableHeight = tableData.length * 21.5 + 40;

    if (
      y + estimatedTableHeight + estimatedHeadingTableHeight >=
      docHeight - 20
    ) {
      doc.addPage();
      doc.addImage(
        logoImg,
        "PNG",
        docWidth - logoWidth - 40,
        20,
        logoWidth,
        logoHeight,
        null,
        "SLOW",
      );
      yPos = logoHeight + 45;
      y = yPos;
    }

    doc.setFontSize(headerSize);
    doc.autoTable({
      startY: y,
      columnStyles: {
        0: { cellWidth: "auto" },
      },
      styles: {
        cellPadding: 5,
        lineWidth: 1,
        fillColor: [237, 36, 43],
        lineColor: [0, 0, 0],
      },
      head: [tableHeader.map((header) => header)],
      didDrawPage: function (data) {
        yPos = data.cursor.y;
      },
    });

    y = yPos;

    doc.setFontSize(size);
    doc.autoTable({
      startY: y,
      columnStyles: {
        0: { cellWidth: 150, fontStyle: "bold", textColor: [0, 0, 0] },
        1: { cellWidth: 108, textColor: [0, 0, 0] },
        2: { cellWidth: 150, fontStyle: "bold", textColor: [0, 0, 0] },
        3: { cellWidth: "auto", textColor: [0, 0, 0] },
      },
      styles: {
        cellPadding: 5,
        lineWidth: 1,
        fillColor: [255, 255, 255],
        lineColor: [0, 0, 0],
      },
      body: tableData.map((detail) => [
        detail.title1,
        detail.data1,
        detail.title2,
        detail.data2,
      ]),
      didDrawPage: function (data) {
        yPos = data.cursor.y + spacing;
      },
    });
  };

  // ===========================================
  // Start building the PDF
  // ===========================================

  // Add Logo
  doc.addImage(
    logoImg,
    "PNG",
    docWidth - logoWidth - 40,
    20,
    logoWidth,
    logoHeight,
    null,
    "SLOW",
  );
  // yPos = logoHeight + 45;
  yPos = 40;

  // Form Name
  addTextToPdf(formName, 14, "normal", 40, yPos, 10);

  // Personal Details
  const personalDetails = [
    {
      title1: "Title",
      data1: data.title,
      title2: "Initials",
      data2: data.initials,
    },
    {
      title1: "First Name",
      data1: data.firstName,
      title2: "Surname",
      data2: data.lastName,
    },
    {
      title1: "ID Number",
      data1: data.idNumber,
      title2: "ID Type",
      data2: data.idType,
    },
    {
      title1: "Issuing Country",
      data1: data.idCountry,
      title2: "Date of Birth",
      data2: data.dob,
    },
    {
      title1: "Gender",
      data1: data.gender,
      title2: "Marital Status",
      data2: data.maritalStatus,
    },
  ];

  const contactDetails = [
    {
      title1: "Mobile Telephone Number",
      data1: data.mobile,
      title2: "Business Telephone Number",
      data2: data.workTelNo,
    },
    {
      title1: "Home Telephone Number",
      data1: data.homeTelNo,
      title2: "Email",
      data2: data.email,
    },
    {
      title1: "Preferred Communication Type",
      data1: data.preferredCommunicationType,
    },
  ];

  const addressDetails = [
    {
      title1: "Unit/Complex",
      data1: data.complex,
      title2: "Street Address",
      data2: data.streetAddress,
    },
    { title1: "Suburb", data1: data.suburb, title2: "City", data2: data.city },
    {
      title1: "Province",
      data1: data.province,
      title2: "Postal Code",
      data2: data.postalCode,
    },
  ];

  const postalAddressDetails = [
    {
      title1: "Postal Address Unit/Complex",
      data1: data.postalComplex,
      title2: "Postal Address",
      data2: data.postalAddress,
    },
    {
      title1: "Suburb",
      data1: data.postalSuburb,
      title2: "City",
      data2: data.postalCity,
    },
    {
      title1: "Province",
      data1: data.postalProvince,
      title2: "Postal Code",
      data2: data.postalPostalCode,
    },
  ];

  addFourColumnTableToPdf(
    ["Personal Details"],
    12,
    personalDetails,
    10,
    "normal",
    40,
    yPos,
    20,
  );

  addFourColumnTableToPdf(
    ["Contact Information"],
    12,
    contactDetails,
    10,
    "normal",
    40,
    yPos,
    20,
  );

  addFourColumnTableToPdf(
    ["Physical Address Information"],
    12,
    addressDetails,
    10,
    "normal",
    40,
    yPos,
    20,
  );

  addFourColumnTableToPdf(
    ["Postal Address Information"],
    12,
    postalAddressDetails,
    10,
    "normal",
    40,
    yPos,
    20,
  );

  doc.addPage();
  doc.addImage(
    logoImg,
    "PNG",
    docWidth - logoWidth - 40,
    20,
    logoWidth,
    logoHeight,
    null,
    "SLOW",
  );
  yPos = logoHeight + 45;

  // Terms and Conditions
  addTextToPdf("Terms and Conditions", 12, "bold", 40, yPos, 15);
  addTextToPdf(data.termsAndConditions, 10, "normal", 40, yPos, 20);

  addTextToPdf(
    `Signed at ${data.signedLocation} on ${data.signedDate}`,
    10,
    "normal",
    40,
    yPos,
    15,
  );

  // Signature
  if (signatureImage) {
    doc.addImage(signatureImage, "PNG", 40, yPos, 200, 60, null, "SLOW");
    yPos += 70;
  }

  addTextToPdf(
    `Applicant Signature: ${data.firstName} ${data.lastName}`,
    10,
    "normal",
    40,
    yPos,
    10,
  );

  addTextToPdf(`IP Address: ${data.ip}`, 10, "normal", 40, yPos, 10);

  if (data.position.latitude && data.position.longitude) {
    addTextToPdf(
      `Geo Location: Latitude: ${data.position.latitude}, Longitude: ${data.position.longitude}`,
      10,
      "normal",
      40,
      yPos,
      10,
    );
  }

  // Return PDF as blob and base64
  const pdfBlob = doc.output("blob");
  const pdfBase64 = doc.output("dataurlstring").split(",")[1];

  return {
    blob: pdfBlob,
    base64: pdfBase64,
    doc: doc, // For advanced usage if needed
  };
}
